import {
  CardItemTypes,
  IAudioCardItem,
  ICheckListCardItem,
  IGifCardItem,
  IImageCardItem,
  ILinkCardItem,
  IMultipleChoiceCardItem,
  IOpenQuestionCardItem,
  IPollCardItem,
  ITextCardItem,
  IVideoCardItem,
  ListStyles,
} from 'types/card';

import { getScaledRelativeDimensions } from './relativeDimensions';
import { getShortUniqueId } from './uniqueId';

export const textItemFactory = (
  type: 'headline' | 'paragraph',
  display: ITextCardItem['body']['display'],
  styles: {
    fontSize: number;
    listStyle?: ListStyles;
    fontWeight: 'lighter' | 'normal' | 'bold';
    textAlign: 'left' | 'center' | 'right';
    color: '#FFFFFF' | '#000000';
  },
): ITextCardItem => {
  const { fontSize, listStyle, ...restStyles } = styles;

  return {
    id: getShortUniqueId(),
    meta: {
      type: CardItemTypes.TEXT,
      version: 1,
    },
    body: {
      type,
      display,
      content: '',
      fontSize,
      listStyle: listStyle || ListStyles.NONE,
      styles: {
        backgroundColor: 'transparent',
        ...restStyles,
      },
    },
    dimensions: {
      width: '90%',
      height: 'auto',
    },
    position: {
      top: type === 'headline' ? '25%' : '35%',
      left: '5%',
    },
  };
};

export const audioItemFactory = (
  body: IAudioCardItem['body'],
): IAudioCardItem => {
  const dimensions = getScaledRelativeDimensions(300, 300);

  return {
    id: getShortUniqueId(),
    meta: {
      type: CardItemTypes.AUDIO,
      version: 1,
    },
    body,
    dimensions,
    position: {
      top: `calc(50% - (${dimensions.height} / 2))`,
      left: `calc(50% - (${dimensions.width} / 2))`,
    },
  };
};

export const gifItemFactory = (body: IGifCardItem['body']): IGifCardItem => {
  const { originalWidth, originalHeight } = body;
  const dimensions = getScaledRelativeDimensions(
    +originalWidth!,
    +originalHeight!,
  );

  return {
    id: getShortUniqueId(),
    meta: {
      type: CardItemTypes.GIF,
      version: 1,
    },
    body,
    dimensions,
    position: {
      top: `calc(50% - (${dimensions.height} / 2)`,
      left: `calc(50% - (${dimensions.width} / 2)`,
    },
  };
};

export const imageItemFactory = (
  body: IImageCardItem['body'],
): IImageCardItem => {
  const { originalWidth, originalHeight } = body;
  const dimensions = getScaledRelativeDimensions(
    originalWidth!,
    originalHeight!,
  );

  return {
    id: getShortUniqueId(),
    meta: {
      type: CardItemTypes.IMAGE,
      version: 1,
    },
    body,
    dimensions,
    position: {
      top: `calc(50% - (${dimensions.height} / 2)`,
      left: `calc(50% - (${dimensions.width} / 2)`,
    },
  };
};

export const videoItemFactory = (
  body: IVideoCardItem['body'],
  dimensions: {
    width: number;
    height: number;
    aspectRatio?: number;
  },
): IVideoCardItem => {
  const { width, height, aspectRatio } = dimensions;

  const relativeDimensions = getScaledRelativeDimensions(width, height);

  return {
    id: getShortUniqueId(),
    meta: {
      type: CardItemTypes.VIDEO,
      version: 1,
    },
    body,
    dimensions: {
      ...relativeDimensions,
      aspectRatio,
    },
    position: {
      top: `calc(50% - (${relativeDimensions.height} / 2)`,
      left: `calc(50% - (${relativeDimensions.width} / 2)`,
    },
  };
};

export const linkItemFactory = (body: ILinkCardItem['body']): ILinkCardItem => {
  return {
    id: getShortUniqueId(),
    meta: {
      type: CardItemTypes.LINK,
      version: 1,
    },
    dimensions: {
      width: '40%',
      height: 'auto',
    },
    body,
    position: {
      top: 'calc(50%)',
      left: 'calc(30%)',
    },
  };
};

export const multipleChoiceItemFactory = (
  body: IMultipleChoiceCardItem['body'],
): IMultipleChoiceCardItem => {
  return {
    id: getShortUniqueId(),
    meta: {
      type: CardItemTypes.MULTIPLE_CHOICE_QUESTION,
      version: 1,
    },
    dimensions: {
      width: '70%',
      height: 'auto',
    },
    body,
    position: {
      top: '40%',
      left: '15%',
    },
  };
};

export const checkListItemFactory = (
  body: ICheckListCardItem['body'],
): ICheckListCardItem => {
  return {
    id: getShortUniqueId(),
    meta: {
      type: CardItemTypes.CHECK_LIST,
      version: 1,
    },
    dimensions: {
      width: '70%',
      height: 'auto',
    },
    body,
    position: {
      top: '40%',
      left: '15%',
    },
  };
};

export const openQuestionItemFactory = (
  body: IOpenQuestionCardItem['body'],
): IOpenQuestionCardItem => {
  return {
    id: getShortUniqueId(),
    meta: {
      type: CardItemTypes.OPEN_QUESTION,
      version: 1,
    },
    dimensions: {
      width: '70%',
      height: 'auto',
    },
    body,
    position: {
      top: '40%',
      left: '15%',
    },
  };
};

export const pollItemFactory = (body: IPollCardItem['body']): IPollCardItem => {
  return {
    id: getShortUniqueId(),
    meta: {
      type: CardItemTypes.POLL,
      version: 1,
    },
    dimensions: {
      width: '70%',
      height: 'auto',
    },
    body,
    position: {
      top: '40%',
      left: '15%',
    },
  };
};

export const getCardTextItem = ({
  order,
  top,
  itemType,
  textType,
  content,
  color,
  fontSize,
  fontWeight,
}: {
  order: number;
  top: string;
  itemType:
    | CardItemTypes.STORY_TITLE
    | CardItemTypes.STORY_DESCRIPTION
    | CardItemTypes.TEXT;
  textType: 'headline' | 'paragraph';
  content: string;
  color: string;
  fontSize: number;
  fontWeight: 'bold' | 'normal';
}): ITextCardItem => {
  return {
    id: getShortUniqueId(),
    meta: {
      version: 1,
      order,
      type: itemType,
    },
    position: {
      top,
      left: '5%',
    },
    dimensions: {
      width: '90%',
      height: 'auto',
    },
    body: {
      content,
      display: 'p',
      fontSize,
      type: textType,
      styles: {
        backgroundColor: 'transparent',
        color,
        fontWeight,
        textAlign: 'center',
      },
    },
  };
};
